
import React from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { User, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface CampaignSubTypeSelectorProps {
  selectedType: string;
  onTypeSelect: (type: 'single' | 'double') => void;
  onNext?: () => void;
}

const CampaignSubTypeSelector: React.FC<CampaignSubTypeSelectorProps> = ({
  selectedType,
  onTypeSelect,
  onNext
}) => {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h3 className="text-lg font-medium">Campaign Sub-type</h3>
        <p className="text-sm text-gray-500">
          Select the structure of your referral campaign
        </p>
      </div>
      
      <RadioGroup
        value={selectedType}
        onValueChange={(value) => onTypeSelect(value as 'single' | 'double')}
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        <div>
          <RadioGroupItem
            value="single"
            id="single"
            className="peer sr-only"
          />
          <Label
            htmlFor="single"
            className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
          >
            <Card className="w-full border-0 shadow-none">
              <CardHeader className="pb-2">
                <div className="flex items-center space-x-2">
                  <div className="bg-green-100 p-2 rounded-full">
                    <User className="h-5 w-5 text-green-600" />
                  </div>
                  <CardTitle className="text-base">Single-Sided Referral</CardTitle>
                </div>
                <CardDescription className="pt-1">Single Pay</CardDescription>
              </CardHeader>
              <CardContent className="text-sm">
                <p>Only the referrer receives a reward when a referred user completes the required action.</p>
              </CardContent>
              <CardFooter className="pt-0">
                <div className="text-xs text-gray-500 mt-2 space-y-1">
                  <div className="flex items-center">
                    <span className="font-semibold mr-1">Database Schema:</span> 
                    <code className="text-xs bg-gray-100 p-1 rounded">
                      {`{ "referral_type": "single", "reward_referrer": true, "reward_referred": false }`}
                    </code>
                  </div>
                  <div>
                    <span className="font-semibold">Example:</span> Airbnb
                  </div>
                </div>
              </CardFooter>
            </Card>
          </Label>
        </div>
        
        <div>
          <RadioGroupItem
            value="double"
            id="double"
            className="peer sr-only"
          />
          <Label
            htmlFor="double"
            className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
          >
            <Card className="w-full border-0 shadow-none">
              <CardHeader className="pb-2">
                <div className="flex items-center space-x-2">
                  <div className="bg-blue-100 p-2 rounded-full">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <CardTitle className="text-base">Double-Sided Referral</CardTitle>
                </div>
                <CardDescription className="pt-1">Double Pay</CardDescription>
              </CardHeader>
              <CardContent className="text-sm">
                <p>Both the referrer and the referred customer receive a reward upon successful conversion.</p>
              </CardContent>
              <CardFooter className="pt-0">
                <div className="text-xs text-gray-500 mt-2 space-y-1">
                  <div className="flex items-center">
                    <span className="font-semibold mr-1">Database Schema:</span> 
                    <code className="text-xs bg-gray-100 p-1 rounded">
                      {`{ "referral_type": "double", "reward_referrer": true, "reward_referred": true }`}
                    </code>
                  </div>
                  <div>
                    <span className="font-semibold">Example:</span> Uber ("Give $10, Get $10")
                  </div>
                </div>
              </CardFooter>
            </Card>
          </Label>
        </div>
      </RadioGroup>
      
      <div className="pt-4">
        <h4 className="text-sm font-medium text-gray-700">Expected Flow:</h4>
        {selectedType === 'single' ? (
          <ol className="list-decimal ml-5 mt-2 text-sm text-gray-600 space-y-1">
            <li>Referrer sends referral</li>
            <li>Referred user completes conversion</li>
            <li>System triggers reward only for the referrer</li>
          </ol>
        ) : (
          <ol className="list-decimal ml-5 mt-2 text-sm text-gray-600 space-y-1">
            <li>Referrer sends referral</li>
            <li>Referred user completes conversion</li>
            <li>System triggers reward for both referrer and referred user</li>
          </ol>
        )}
      </div>

      <div className="flex justify-end pt-6">
        <Button 
          onClick={onNext} 
          disabled={!selectedType}
          size="lg"
        >
          Next: Campaign Settings
        </Button>
      </div>
    </div>
  );
};

export default CampaignSubTypeSelector;
